import { Injectable } from '@angular/core';
import { FetchResult } from '@apollo/client/core';
import { Apollo } from 'apollo-angular';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import {
  CrossReferenceDataType,
  Product,
} from '../../../types/product/product';
import { Recipient } from '../../../types/product/recipients';
import { RespondListDataType } from '../../../types/respond/respond';
import { Participant } from '../../../types/rfp/participant';
import { Rfp, RfpRecipient } from '../../../types/rfp/rfp';
import { RfpPublishDetail } from '../../../types/rfp/rfp-publish-detail';
import { RfpStatus } from '../../../types/rfp/rfp-status';
import { RfpByOrgId } from '../../../types/rfp/rfps-by-org-id';
import { ApolloInstances } from '../graphql.module';
import ADD_CATEGORY from './mutations/addCategory';
import CANCEL_RFP from './mutations/cancel-rfp';
import CREATE_RFP from './mutations/createRfp';
import DISQUALIFY_SELLER from './mutations/disqualifySeller';
import EXPIRE_RFP_TODAY from './mutations/expire-rfp';
import REMOVE_CATEGORY from './mutations/removeCategory';
import RENAME_RFP_CATEGORY from './mutations/renameRfpCategory';
import REQUEST_ADDITIONAL_OFFERS from './mutations/requestAdditionalOffers';
import UPDATE_CUSTOM_SPEND_DATE_RANGE from './mutations/update-custom-spend-date-range';
import UPDATE_CATEGORY_FORMULARY_PRICE from './mutations/updateCategoryFormularyPrice';
import UPDATE_CONTRACT_PREFERENCES from './mutations/updateContractPreferences';
import UPDATE_PRODUCT_FORMULARY_PRICE from './mutations/updateProductFormularyPrice';
import UPDATE_PUBLISH_RFP_DETAILS from './mutations/updatePublishRfp';
import EXTEND_RFP_RESPONSE_DUE_DATE from './mutations/updateResponseDueDate';
import GET_ALL_RFP from './queries/allRfp';
import GET_RFP_CROSS_REFERENCE_DOWNLOAD from './queries/get-cross-reference-download';
import GET_EXECUTE_RFP_STATISTICS from './queries/get-execute-rfp-statistics';
import { POST_RFP_RESPONSES_OID_DOWNLOAD_URL } from './queries/get-post-rfp-responses-download';
import GET_POTENTIAL_SELLERS from './queries/get-potential-sellers';
import { GET_PRODUCTS_BY_PRODUCT_ID } from './queries/get-products-by-product-id';
import GET_RESPONSE_RFP_STATISTICS from './queries/get-response-rfp-statistics';
import GET_RFP from './queries/get-rfp';
import GET_RFP_BASIC_DATA from './queries/get-rfp-basic-data';
import GET_RFP_DETAILS from './queries/get-rfp-details';
import GET_RFP_PARTICIPANT_DOWNLOAD from './queries/get-rfp-participant-download';
import GET_RFP_PARTICIPANTS from './queries/get-rfp-participants';
import GET_RFP_PUBLISH_DETAIL from './queries/get-rfp-published-detail';
import GET_RFP_RECIPENTS from './queries/get-rfp-recipients';
import GET_RFP_BY_ORG_ID from './queries/get-rfps-by-organization-id';
import GET_RFP_CATEGORIES from './queries/getCategoriesByRfp';
import GET_LEAF_NODE_CATEGORIES from './queries/getLeafNodeCategoriesByRfpId';
import GET_RFP_PARTICIPANTS_WITH_RECIPIENT_SPEND_BY_RFP from './queries/getParticipantsByRfp';
import GET_POTENTIAL_CATEGORIES from './queries/getPotentialCategoriesByRfp';
import GET_PRODUCTS_BY_RFP from './queries/getProductsByRfp';
import GET_PRODUCTS_STATISTICS_BY_RFP from './queries/getProductWithStatisticsByRfp';
import { PURCHASE_HISTORY_DOWNLOAD_URL } from './queries/getPurchaseHistoryDownload';
import GET_RESPOND_BY_ORG_ID from './queries/getRespondByOrganizationId';
import SUBMIT_RFP from './queries/submit-rfp';
import {
  FormularyPrice,
  LeafNodeCategory,
  PotentialSeller,
  RetractOffersRequest,
  RfpCategory,
  RfpExecuteStatistics,
  RfpResponseStatistics,
  SetRfpPublishDetailsParams,
  SetRfpResponseDueDateParams,
} from './rfp';
import RETRACT_OFFER_PRICING from './mutations/retract-offer';
import {
  CategorizationScheme,
  getCategorySchemeNameById,
  PublicCategorizationSchemes,
} from '@aptitude/shared-vanilla';
export enum ProductSource {
  APTITUDE = 'aptitude',
  VIZIENT = 'vizient',
}

interface AddCategoryParams {
  addCategory: { id: number; categoryId: number };
}
interface SubmitRfpRequest {
  id: number;
}

interface RenameRfpCategoryParams {
  renameRfpCategory: { id: number; name: string };
}

interface UpdateContractPreferencesParams {
  updateContractPreferences: {
    id: number;
    preferredContractDuration: string;
    preferredContractEffectiveDate: string;
  };
}
export interface CreateRfpParams {
  buyerId: number;
  categoryId?: number;
  categorizationSchemeId?: number;
  sellerId?: number;
}

@Injectable({
  providedIn: 'root',
})
export class RfpGraphqlService {
  constructor(private apollo: Apollo) {}

  private validateParamIsCorrect(param: string, name: string) {
    if (!param?.length || param?.length === 0) {
      throw new Error(
        `The dev is trying to do a grapqhl request with an invalid ${name}: ${param}`,
      );
    }
    return true;
  }

  getProductsByRfp(params: {
    rfpId: string;
    includeFormularyPrice: boolean;
  }): Observable<Array<Product>> {
    this.validateParamIsCorrect(params.rfpId, 'rfpId');

    return this.apollo
      .use(ApolloInstances.RFP)
      .watchQuery<{ optimizedRfp: { products: Array<Product> } }>({
        fetchPolicy: 'no-cache',
        variables: {
          rfpId: params.rfpId,
          includeFormularyPrice: params.includeFormularyPrice,
        },
        query: GET_PRODUCTS_BY_RFP,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => request.data.optimizedRfp.products),
      );
  }

  getDistributorProductsByRfpIdAndBaseProductId(
    rfpId: number,
    baseProductId: number,
  ): Observable<Product[]> {
    return this.apollo
      .use(ApolloInstances.RFP)
      .query<{ rfpProductsByBaseProductId: Product[] }>({
        fetchPolicy: 'no-cache',
        variables: {
          rfpId,
          baseProductId,
        },
        query: GET_PRODUCTS_BY_PRODUCT_ID,
      })
      .pipe(map((response) => response.data.rfpProductsByBaseProductId ?? []));
  }

  getProductsWithStatisticsByRfp(
    rfpId: string,
  ): Observable<CrossReferenceDataType> {
    this.validateParamIsCorrect(rfpId, 'rfpId');

    return this.apollo
      .use(ApolloInstances.RFP)
      .watchQuery<{
        optimizedRfp: CrossReferenceDataType;
      }>({
        fetchPolicy: 'no-cache',
        variables: { rfpId },
        query: GET_PRODUCTS_STATISTICS_BY_RFP,
      })
      .valueChanges.pipe(
        map((request) => request.data.optimizedRfp),
        catchError(() => {
          return of({
            products: [],
            recipients: [],
          } as CrossReferenceDataType);
        }),
      );
  }

  getPotentialCategoriesByRfpId(rfpId: string): Observable<RfpCategory[]> {
    this.validateParamIsCorrect(rfpId, 'rfpId');

    return this.apollo
      .use(ApolloInstances.RFP)
      .watchQuery<{ potentialCategories: RfpCategory[] }>({
        fetchPolicy: 'no-cache',
        variables: { rfpId },
        query: GET_POTENTIAL_CATEGORIES,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => request.data.potentialCategories),
      );
  }
  getCategoriesByRfpId(rfpId: string): Observable<RfpCategory[]> {
    this.validateParamIsCorrect(rfpId, 'rfpId');

    return this.apollo
      .use(ApolloInstances.RFP)
      .watchQuery<{ rfp: { categories: RfpCategory[] } }>({
        fetchPolicy: 'no-cache',
        variables: { rfpId },
        query: GET_RFP_CATEGORIES,
      })
      .valueChanges.pipe(
        take(1),
        map((request) =>
          request.data.rfp.categories.map(
            (category) => category as RfpCategory,
          ),
        ),
      );
  }

  getPotentialSellersByRfpId(rfpId: string): Observable<PotentialSeller[]> {
    this.validateParamIsCorrect(rfpId, 'rfpId');

    return this.apollo
      .use(ApolloInstances.RFP)
      .watchQuery<{ potentialSellers: PotentialSeller[] }>({
        fetchPolicy: 'no-cache',
        variables: { rfpId },
        query: GET_POTENTIAL_SELLERS,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => request.data.potentialSellers),
      );
  }

  getLeafNodeCategoriesByRfpId(rfpId: string): Observable<LeafNodeCategory[]> {
    this.validateParamIsCorrect(rfpId, 'rfpId');

    return this.apollo
      .use(ApolloInstances.RFP)
      .watchQuery<{ rfp: { leafNodeCategories: LeafNodeCategory[] } }>({
        fetchPolicy: 'no-cache',
        variables: { rfpId },
        query: GET_LEAF_NODE_CATEGORIES,
      })
      .valueChanges.pipe(map((request) => request.data.rfp.leafNodeCategories));
  }

  /*
   * @description Update the target price for a category.
   */
  updateCategoryFormularyPrice(
    rfpId: string,
    formularyPrices: FormularyPrice[],
  ): Observable<any> {
    this.validateParamIsCorrect(rfpId, 'rfpId');

    return this.apollo.use(ApolloInstances.RFP).mutate({
      fetchPolicy: 'no-cache',
      variables: { rfpId, formularyPrices },
      mutation: UPDATE_CATEGORY_FORMULARY_PRICE,
    });
  }

  /*
   * @description Update the target price for a Product.
   */
  updateProductFormularyPrice(
    rfpId: string,
    formularyPrices: FormularyPrice[],
  ): Observable<any> {
    this.validateParamIsCorrect(rfpId, 'rfpId');

    return this.apollo.use(ApolloInstances.RFP).mutate({
      fetchPolicy: 'no-cache',
      variables: { rfpId, formularyPrices },
      mutation: UPDATE_PRODUCT_FORMULARY_PRICE,
    });
  }

  /**
   * Retrieves an RFP (Request for Proposal) by its ID.
   *
   * @param rfpId - The unique identifier of the RFP to retrieve.
   * @param useNewPscLabel - Optional flag to determine whether to use the new PSC label.
   *                          If true, and the categorization scheme ID matches a specific value,
   *                          the categorization scheme name will be updated accordingly.
   *                          Defaults to `false`.
   * @returns An observable that emits the retrieved RFP object.
   * @throws Will throw an error if the provided `rfpId` is invalid.
   */
  getRfp(rfpId: string, useNewPscLabel = false): Observable<Rfp> {
    this.validateParamIsCorrect(rfpId, 'rfpId');

    return this.apollo
      .use(ApolloInstances.RFP)
      .watchQuery<{ rfp: Rfp }>({
        fetchPolicy: 'no-cache',
        variables: { rfpId },
        query: GET_RFP,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => {
          if (useNewPscLabel) {
            const schemeId = request.data.rfp.category.categorizationScheme.id;
            if (schemeId === PublicCategorizationSchemes.CC) {
              request.data.rfp.category.categorizationScheme.name = 'CC';
            }
          }
          return new Rfp(request.data.rfp);
        }),
      );
  }

  /**
   * Retrieves the basic data for a specific RFP (Request for Proposal) by its ID.
   *
   * @param rfpId - The unique identifier of the RFP to fetch.
   * @param useNewPscLabel - Optional flag to determine whether to use the new PSC label.
   *                          If true, and the categorization scheme ID matches a specific value, the categorization
   *                          scheme name will be updated accordingly.
   * @returns An `Observable` that emits the RFP data wrapped in an `Rfp` instance.
   *
   * @throws Will throw an error if the `rfpId` parameter is invalid.
   */
  getRfpBasicData(rfpId: string, useNewPscLabel = false): Observable<Rfp> {
    this.validateParamIsCorrect(rfpId, 'rfpId');

    return this.apollo
      .use(ApolloInstances.RFP)
      .watchQuery<{ rfp: Rfp }>({
        fetchPolicy: 'no-cache',
        variables: { rfpId },
        query: GET_RFP_BASIC_DATA,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => {
          if (useNewPscLabel) {
            const schemeId = request.data.rfp.category.categorizationScheme.id;
            if (schemeId === PublicCategorizationSchemes.CC) {
              request.data.rfp.category.categorizationScheme.name = 'CC';
            }
          }
          return new Rfp(request.data.rfp);
        }),
      );
  }

  getRfpDetails(rfpId: string): Observable<Rfp> {
    this.validateParamIsCorrect(rfpId, 'rfpId');

    return this.apollo
      .use(ApolloInstances.RFP)
      .watchQuery<{ rfp: Rfp }>({
        fetchPolicy: 'no-cache',
        variables: { rfpId },
        query: GET_RFP_DETAILS,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => new Rfp(request.data.rfp)),
      );
  }

  getAllRfps(): Observable<Array<Rfp>> {
    return this.apollo
      .use(ApolloInstances.RFP)
      .watchQuery<{ rfps: Array<Rfp> }>({
        fetchPolicy: 'no-cache',
        query: GET_ALL_RFP,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => request.data.rfps.map((rfp) => new Rfp(rfp))),
      );
  }
  /**
   * Returns the rfp participants by rfpId (for the buyer side)
   *
   * @param {number} rfpId The Id of the rfp.
   * @return {Observable<Participant[] | undefined>} observable of the participants list.
   */
  getRfpParticipants(rfpId: string): Observable<Participant[] | undefined> {
    this.validateParamIsCorrect(rfpId, 'rfpId');

    return this.apollo
      .use(ApolloInstances.RFP)
      .watchQuery<{ rfp: Rfp }>({
        fetchPolicy: 'no-cache',
        variables: { rfpId },
        query: GET_RFP_PARTICIPANTS,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => request.data.rfp.participants),
      );
  }

  /**
   * Returns the rfp recipients by rfpId
   *
   * @param {number} rfpId The Id of the rfp.
   * @return {Observable<Recipient[] | undefined>} observable of the recipient list.
   */
  getRfpRecipients(rfpId: string): Observable<RfpRecipient[] | undefined> {
    this.validateParamIsCorrect(rfpId, 'rfpId');

    return this.apollo
      .use(ApolloInstances.RFP)
      .watchQuery<{ rfp: Rfp }>({
        fetchPolicy: 'no-cache',
        variables: { rfpId },
        query: GET_RFP_RECIPENTS,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => request.data.rfp.recipients),
      );
  }
  /**
   * Returns the rfp participants with recipient spend by rfpId (for the seller side)
   *
   * @param {number} rfpId The Id of the rfp.
   * @return {Observable<Participant[] | undefined>} observable of the participants list.
   */
  getParticipantsWithRecipientSpend(
    rfpId: string,
  ): Observable<Participant[] | undefined> {
    this.validateParamIsCorrect(rfpId, 'rfpId');

    return this.apollo
      .use(ApolloInstances.RFP)
      .query<{ rfp: Rfp }>({
        fetchPolicy: 'no-cache',
        variables: { rfpId },
        query: GET_RFP_PARTICIPANTS_WITH_RECIPIENT_SPEND_BY_RFP,
      })
      .pipe(map((request) => request.data.rfp.participants));
  }

  addCategory(
    rfpId: string,
    categoryId: string,
  ):
    | Observable<
        FetchResult<AddCategoryParams, Record<string, any>, Record<string, any>>
      >
    | undefined {
    this.validateParamIsCorrect(rfpId, 'rfpId');
    this.validateParamIsCorrect(categoryId, 'categoryId');

    return this.apollo.use(ApolloInstances.RFP).mutate<AddCategoryParams>({
      fetchPolicy: 'no-cache',
      variables: { id: rfpId, categoryId },
      mutation: ADD_CATEGORY,
    });
  }

  removeCategory(
    rfpId: string,
    categoryId: string,
  ):
    | Observable<
        FetchResult<AddCategoryParams, Record<string, any>, Record<string, any>>
      >
    | undefined {
    this.validateParamIsCorrect(rfpId, 'rfpId');
    this.validateParamIsCorrect(categoryId, 'categoryId');

    return this.apollo.use(ApolloInstances.RFP).mutate<AddCategoryParams>({
      fetchPolicy: 'no-cache',
      variables: { id: rfpId, categoryId },
      mutation: REMOVE_CATEGORY,
    });
  }

  /*
   * @description Updates the contract duration and contract effective date
   */
  updateContractPreferences(
    rfpId: string,
    preferredContractDuration: number,
    preferredContractEffectiveDate: string,
    responseDueDate: string,
  ): Observable<Record<string, any>> {
    this.validateParamIsCorrect(rfpId, 'rfpId');
    this.validateParamIsCorrect(
      preferredContractEffectiveDate,
      'preferredContractEffectiveDate',
    );
    this.validateParamIsCorrect(responseDueDate, 'responseDueDate');
    return this.apollo
      .use(ApolloInstances.RFP)
      .mutate<UpdateContractPreferencesParams>({
        fetchPolicy: 'no-cache',
        variables: {
          id: rfpId,
          preferredContractDuration,
          preferredContractEffectiveDate,
          whenResponsesDue: responseDueDate,
        },
        mutation: UPDATE_CONTRACT_PREFERENCES,
      });
  }

  /*
   * @description Renames the category name for the selected rfp
   */
  renameRfpCategory(
    rfpId: number,
    name: string,
  ): Observable<Record<string, any>> {
    this.validateParamIsCorrect(rfpId.toString(), 'rfpId');
    this.validateParamIsCorrect(name, 'name');

    return this.apollo
      .use(ApolloInstances.RFP)
      .mutate<{ renameRfpCategory: Record<string, any> }>({
        fetchPolicy: 'no-cache',
        variables: { id: rfpId, name },
        mutation: RENAME_RFP_CATEGORY,
      })
      .pipe(map((request) => request.data!.renameRfpCategory));
  }

  updateCustomSpendDateRange(
    rfpId: string,
    startDate: string,
    endDate: string,
    categorizationScheme: CategorizationScheme[] = [],
  ): Observable<Rfp> {
    return this.apollo
      .use(ApolloInstances.RFP)
      .mutate<{ updateRfpSpendDateRange: Rfp }>({
        fetchPolicy: 'no-cache',
        variables: {
          id: rfpId,
          customSpendStartDate: startDate,
          customSpendEndDate: endDate,
        },
        mutation: UPDATE_CUSTOM_SPEND_DATE_RANGE,
      })
      .pipe(
        map((request) => {
          const { category } = request.data!.updateRfpSpendDateRange;
          if (
            categorizationScheme.length > 0 &&
            category.categorizationScheme.id === PublicCategorizationSchemes.CC
          ) {
            const updatedCategory = {
              ...category,
              categorizationScheme: {
                ...category.categorizationScheme,
                name: getCategorySchemeNameById(
                  category.categorizationScheme.id,
                  categorizationScheme,
                ),
              },
            };

            return new Rfp({
              ...request.data!.updateRfpSpendDateRange,
              category: updatedCategory,
            });
          }

          return new Rfp(request.data!.updateRfpSpendDateRange);
        }),
      );
  }

  getPurchaseHistoryDownload(rfpId: number) {
    return this.apollo
      .use(ApolloInstances.RFP)
      .query<{ purchaseHistoryDownload: { url: string } }>({
        fetchPolicy: 'no-cache',
        query: PURCHASE_HISTORY_DOWNLOAD_URL,
        variables: {
          rfpId,
        },
      })
      .pipe(map((request) => request.data.purchaseHistoryDownload.url));
  }

  getRfpParticipantDownload(rfpId: string): Observable<string> {
    this.validateParamIsCorrect(rfpId, 'rfpId');

    return this.apollo
      .use(ApolloInstances.RFP)
      .watchQuery<{ rfpParticipantDownload: { url: string } }>({
        fetchPolicy: 'no-cache',
        variables: { rfpId },
        query: GET_RFP_PARTICIPANT_DOWNLOAD,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => request.data.rfpParticipantDownload.url),
      );
  }

  getCrossReferenceDownload(rfpId: string): Observable<string> {
    this.validateParamIsCorrect(rfpId, 'rfpId');

    return this.apollo
      .use(ApolloInstances.RFP)
      .watchQuery<{ rfpCrossReferenceDownload: { url: string } }>({
        fetchPolicy: 'no-cache',
        variables: { rfpId },
        query: GET_RFP_CROSS_REFERENCE_DOWNLOAD,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => request.data.rfpCrossReferenceDownload.url),
      );
  }

  downloadResponseOIDReport(rfpId: string): Observable<string> {
    this.validateParamIsCorrect(rfpId, 'rfpId');

    return this.apollo
      .use(ApolloInstances.OFFER)
      .watchQuery<{ offerItemDetailDownload: { url: string } }>({
        fetchPolicy: 'no-cache',
        variables: { rfpId },
        query: POST_RFP_RESPONSES_OID_DOWNLOAD_URL,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => request.data.offerItemDetailDownload.url),
      );
  }

  submitRfpRequest(id: number) {
    return this.apollo
      .use(ApolloInstances.RFP)
      .mutate<{ submitRfpRequest: SubmitRfpRequest }>({
        variables: { id },
        mutation: SUBMIT_RFP,
      });
  }

  getRfpExecuteStatistics(
    organizationId: string,
  ): Observable<RfpExecuteStatistics> {
    this.validateParamIsCorrect(organizationId, 'organizationId');
    return this.apollo
      .use(ApolloInstances.RFP)
      .watchQuery<{
        rfpStatistics: RfpExecuteStatistics;
      }>({
        fetchPolicy: 'no-cache',
        variables: { organizationId },
        query: GET_EXECUTE_RFP_STATISTICS,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => {
          return request.data.rfpStatistics;
        }),
      );
  }

  getRfpResponseStatistics(
    organizationId: string,
  ): Observable<RfpResponseStatistics> {
    this.validateParamIsCorrect(organizationId, 'organizationId');
    return this.apollo
      .use(ApolloInstances.RFP)
      .watchQuery<{
        rfpStatistics: RfpResponseStatistics;
      }>({
        fetchPolicy: 'no-cache',
        variables: { organizationId },
        query: GET_RESPONSE_RFP_STATISTICS,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => {
          return request.data.rfpStatistics;
        }),
      );
  }

  createRfp(params: CreateRfpParams): Observable<Record<string, any>> {
    return this.apollo
      .use(ApolloInstances.RFP)
      .mutate<{ createRfp: Record<string, any> }>({
        fetchPolicy: 'no-cache',
        variables: {
          buyerId: params.buyerId,
          categoryId: params.categoryId,
          categorizationSchemeId: params.categorizationSchemeId,
          sellerId: params.sellerId,
        },
        mutation: CREATE_RFP,
      })
      .pipe(map((request) => request.data!.createRfp));
  }

  getRespondByOrgId(
    organizationId: string,
  ): Observable<Array<RespondListDataType>> {
    this.validateParamIsCorrect(organizationId, 'organizationId');
    return this.apollo
      .use(ApolloInstances.RFP)
      .watchQuery<{
        rfpsByOrg: Array<RespondListDataType>;
      }>({
        fetchPolicy: 'no-cache',
        variables: { organizationId },
        query: GET_RESPOND_BY_ORG_ID,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => {
          return request.data?.rfpsByOrg;
        }),
      );
  }

  getRfpsByOrgId(organizationId: string): Observable<Array<RfpByOrgId>> {
    this.validateParamIsCorrect(organizationId, 'organizationId');
    return this.apollo
      .use(ApolloInstances.RFP)
      .watchQuery<{
        rfpsByOrg: Array<RfpByOrgId>;
      }>({
        fetchPolicy: 'no-cache',
        variables: { organizationId },
        query: GET_RFP_BY_ORG_ID,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => {
          return request.data?.rfpsByOrg;
        }),
      );
  }

  setRfpResponseDueDate(details: SetRfpResponseDueDateParams): Observable<Rfp> {
    this.validateParamIsCorrect(details.rfpId.toString(), 'rfpId');
    return this.apollo
      .use(ApolloInstances.RFP)
      .mutate<{
        extendRfpResponseDueDate: Rfp;
      }>({
        fetchPolicy: 'no-cache',
        variables: {
          rfpId: details.rfpId,
          responseDueDate: details.responseDueDate,
        },
        mutation: EXTEND_RFP_RESPONSE_DUE_DATE,
      })
      .pipe(map((request) => request.data!.extendRfpResponseDueDate));
  }

  /*
   * Gets the publish rfp details
   */
  getRfpPublishDetail(rfpId: string): Observable<RfpPublishDetail> {
    this.validateParamIsCorrect(rfpId, 'rfpId');

    return this.apollo
      .use(ApolloInstances.RFP)
      .watchQuery<{ rfpPublishedDetail: RfpPublishDetail }>({
        fetchPolicy: 'no-cache',
        variables: { rfpId },
        query: GET_RFP_PUBLISH_DETAIL,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => request.data.rfpPublishedDetail),
      );
  }

  /**
   * Creates/Updates the publish rfp details
   */
  setPublishRfpDetails(
    details: SetRfpPublishDetailsParams,
  ): Observable<Record<string, any>> {
    this.validateParamIsCorrect(details.rfpId.toString(), 'rfpId');
    return this.apollo
      .use(ApolloInstances.RFP)
      .mutate<{ updateRfpPublishedDetail: Record<string, any> }>({
        fetchPolicy: 'no-cache',
        variables: {
          rfpId: details.rfpId,
          contactName: details.contactName,
          contactEmail: details.contactEmail,
          contactPhone: details.contactPhone,
          projectedRfpDate: details.projectedRfpDate,
          requirements: details.requirements,
          description: details.description,
        },
        mutation: UPDATE_PUBLISH_RFP_DETAILS,
      })
      .pipe(map((request) => request.data!.updateRfpPublishedDetail));
  }

  disqualifySeller(
    rfpId: number,
    sellerIds: number[],
  ): Observable<Record<string, any>> {
    this.validateParamIsCorrect(rfpId.toString(), 'rfpId');
    if (!sellerIds || !Array.isArray(sellerIds) || !sellerIds.length) {
      throw new Error('Invalid sellerIds');
    }

    return this.apollo
      .use(ApolloInstances.RFP)
      .mutate<{ disqualifySeller: Record<string, any> }>({
        fetchPolicy: 'no-cache',
        variables: { rfpId, sellerIds },
        mutation: DISQUALIFY_SELLER,
      })
      .pipe(
        map((request) => {
          return request.data!.disqualifySeller;
        }),
      );
  }

  cancelRfp(rfpId: number) {
    this.validateParamIsCorrect(rfpId.toString(), 'rfpId');
    return this.apollo
      .use(ApolloInstances.RFP)
      .mutate<{
        cancelRfp: {
          id: number;
          status: RfpStatus;
        };
      }>({
        fetchPolicy: 'no-cache',
        variables: { id: rfpId },
        mutation: CANCEL_RFP,
      })
      .pipe(map((request) => request.data!.cancelRfp));
  }

  expireRfpToday(rfpId: number) {
    this.validateParamIsCorrect(rfpId.toString(), 'rfpId');
    return this.apollo
      .use(ApolloInstances.RFP)
      .mutate<{
        expireRfpToday: {
          id: number;
          status: RfpStatus;
        };
      }>({
        fetchPolicy: 'no-cache',
        variables: { id: rfpId },
        mutation: EXPIRE_RFP_TODAY,
      })
      .pipe(map((request) => request.data!.expireRfpToday));
  }

  requestRetraction(rfpId: number, sellerId: number | string) {
    this.validateParamIsCorrect(rfpId.toString(), 'rfpId');
    this.validateParamIsCorrect(sellerId.toString(), 'sellerId');
    return this.apollo
      .use(ApolloInstances.OFFER)
      .mutate<{ retractOffers: { success: boolean } }>({
        fetchPolicy: 'no-cache',
        variables: { input: { rfpId, sellerId } as RetractOffersRequest },
        mutation: RETRACT_OFFER_PRICING,
      })
      .pipe(map((request) => request.data!.retractOffers));
  }

  requestAdditionalOffers(
    rfpId: number,
    responseDueDate: string,
    sellerIds: Array<number>,
  ): Observable<Rfp> {
    this.validateParamIsCorrect(rfpId.toString(), 'rfpId');
    return this.apollo
      .use(ApolloInstances.RFP)
      .mutate<{ requestAdditionalOffer: Rfp }>({
        fetchPolicy: 'no-cache',
        variables: {
          rfpId,
          responseDueDate,
          sellerIds,
        },
        mutation: REQUEST_ADDITIONAL_OFFERS,
      })
      .pipe(map((request) => request.data!.requestAdditionalOffer));
  }
}
